<template>
  <v-container fluid>
    <v-card class="pt-2">
      <div class="d-flex align-center justify-space-between">
        <v-card-title>{{ pageTitle }}</v-card-title>
        <div class="autoimport_header-buttons">
          <v-btn color="grey" @click="close" :disabled="loading">
            {{ $t("Close") }}
          </v-btn>

          <v-btn
            color="primary"
            prepend-icon="mdi-plus"
            @click="addContentTypeAutoImportConfig()"
            :disabled="loading"
          >
            {{ $t("Add") }}
          </v-btn>
        </div>
      </div>
      <v-card-text class="pa-0">
        <v-row class="pa-0" justify="center" align="center" v-if="loading">
          <div class="text-center pb-3">
            <v-progress-circular
              :size="50"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
        </v-row>
        <v-expansion-panels class="mt-3" v-model="activePanel" v-if="!loading">
          <v-expansion-panel
              v-for="(item, index) in contentTypeAutoImportConfigs"
              :key="item.uuid"
          >
            <v-expansion-panel-header>
              {{ getServiceName(item.serviceID) }} - {{ item.description }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                  <v-col cols="auto" align-self="center" class="py-0">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          large
                          color="error"
                          icon
                          @click="removeContentTypeAutoImportConfig(item)"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("Delete") }}</span>
                    </v-tooltip>
                  </v-col>
                  <v-col cols="12" sm="6" md="2" align-self="center" class="py-0">
                    <v-select
                      v-model="item.serviceID"
                      :items="services"
                      item-value="id"
                      item-text="name"
                      :placeholder="$t('Service')"
                      @change="resetValues(item)"
                      :error-messages="serviceErrors(index)"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3" align-self="center" class="py-0">
                    <v-text-field
                      v-model="item.description"
                      :label="$t('Description')"
                      :error-messages="descriptionErrors(index)"
                    />
                  </v-col>
                  <!-- FileSystem -->
                  <FileSystemProviderConfig
                    v-if="item.serviceID == AutoImportSources.FileSystem"
                    :validator.sync="$v.contentTypeAutoImportConfigs.$each[index]"
                    :item="item"
                  />
                  <!-- Email -->
                  <EmailProviderConfig
                    v-if="item.serviceID == AutoImportSources.Email"
                    :validator.sync="$v.contentTypeAutoImportConfigs.$each[index]"
                    :item="item"
                    @on-saved-config="saveEmailConfig"
                    @update-loading="val => item.loading = val"
                  />
                  <!-- OneDrive and EmailOffice365 -->
                  <MicrosoftProvidersConfig
                    v-if="item.serviceID == AutoImportSources.OneDrive || item.serviceID == AutoImportSources.EmailOffice365"
                    :validator.sync="$v.contentTypeAutoImportConfigs.$each[index]"
                    :item="item"
                    @on-folder-selected="onFolderSelected"
                    @update-loading="val => item.loading = val"
                  />
                  <!-- GoogleDrive -->
                  <CloudProvidersConfig
                    v-if="item.serviceID == AutoImportSources.GoogleDrive"
                    :validator.sync="$v.contentTypeAutoImportConfigs.$each[index]"
                    :item="item"
                    @on-folder-selected="onFolderSelected"
                    @update-loading="val => item.loading = val"
                  />
                </v-row>
                <v-row class="ml-3">
                  <v-col cols="12" sm="6" md="3" align-self="center" class="py-0">
                    <v-switch
                      v-model="item.convertImageToPdf"
                      :label="$t('ConvertImageToPdf')"
                    ></v-switch>
                  </v-col>
                  <v-col cols="12" sm="6" md="3" align-self="center" class="py-0">
                    <v-switch
                      v-model="item.convertOfficeToPdf"
                      :label="$t('ConvertOfficeToPdf')"
                    ></v-switch>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="2"
                    align="center"
                    justify="space-around"
                    class="py-0"
                  >
                    <v-btn outlined @click="editFields(item)" depressed>
                      {{ $t("Fields") }}
                      <v-icon v-if="item.fields.length > 0" right>
                        mdi-pencil
                      </v-icon>
                      <v-icon v-if="item.fields.length == 0" right
                      >mdi-plus</v-icon
                      >
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="1"
                    align="center"
                    justify="space-around"
                    class="py-0"
                  >
                    <v-btn outlined @click="addSeparator(item)" depressed>
                      {{ $t("Site.ContentType.AutoImportConfig.Separator") }}
                      <v-icon v-if="item.alphaNumericSeparator" right>
                        mdi-pencil
                      </v-icon>
                      <v-icon v-else right
                      >mdi-plus</v-icon
                      >
                    </v-btn>
                  </v-col>
                </v-row>
                 <v-row class="pa-0 ml-3">
                  <v-col cols="12" sm="6" md="3" align-self="center" class="py-0">
                    <v-tooltip top v-if="item.hasImputationPlugin === false">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                          <v-switch
                            v-model="item.importingImputationRows"
                            :label="$t('Site.Imputation.ImportingImputationRows')"
                            :disabled="true"
                          />
                        </div>
                      </template>
                      <span>{{ $t('Site.Imputation.ConfigureAIPromptTooltip') }}</span>
                    </v-tooltip>
                    <v-switch
                      v-else
                      v-model="item.importingImputationRows"
                      :label="$t('Site.Imputation.ImportingImputationRows')"
                    />
                  </v-col>
                </v-row>
                <div class="footer d-flex justify-end pa-1">
                  <v-btn color="primary" @click="save(item)" :loading="item.loading">{{ item.id ? $t("Update") : $t("Save") }}</v-btn>
                </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <ContentTypeAutoImportConfigFieldsModal
      :ref="fieldsModalKey"
      :contentTypeId="contentType.id"
      :contentTypeFields="contentType.defFields"
      @on-saved="onFieldsSaved"
    />

    <ConfigAlphaNumericSeparator
      :ref="alphaNumericSeparatorModalKey"
      @on-separator-saved="saveAlphaNumericSeparator"
    />
  </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, requiredIf } from 'vuelidate/lib/validators';
import Utils from '@/ecm/common/Utils';
import ContentTypeService from '@/services/ContentTypeService';
import ContentTypeAutoImportConfig from '@/ecm/models/contentType/autoImport/ContentTypeAutoImportConfig';
import ContentTypeAutoImportConfigFieldsModal from '@/components/administration/contentType/autoImport/ContentTypeAutoImportConfigFieldsModal';
import ContentTypeForAutoImportConfigDTO from '@/ecm/models/contentType/autoImport/ContentTypeForAutoImport';
import AutoImportSources from '@/ecm/variables/AutoImportSources';
import ContentTypeAutoImportConfigService from '@/ecm/models/contentType/documentIntelligence/DTO/ContentTypeAutoImportConfig';
import ConfigAlphaNumericSeparator from '@/components/administration/contentType/autoImport/ConfigAlphaNumericSeparator';
import FileSystemProviderConfig from '@/components/administration/contentType/autoImport/providers/FileSystemProviderConfig.vue';
import EmailProviderConfig from '@/components/administration/contentType/autoImport/providers/EmailProviderConfig.vue';
import MicrosoftProvidersConfig from '@/components/administration/contentType/autoImport/providers/MicrosoftProvidersConfig.vue';
import CloudProvidersConfig from '@/components/administration/contentType/autoImport/providers/CloudProvidersConfig.vue';

export default {
    name: 'AutoImportConfig',
    components: {
        ContentTypeAutoImportConfigFieldsModal,
        ConfigAlphaNumericSeparator,
        FileSystemProviderConfig,
        EmailProviderConfig,
        MicrosoftProvidersConfig,
        CloudProvidersConfig
    },
    data() {
        return {
            fieldsModalKey: 'fieldModalKey',
            alphaNumericSeparatorModalKey: 'alphaNumericSeparatorModalKey',
            currentItem: new ContentTypeAutoImportConfig(),
            hasAutomaticRecognition: false,
            contentTypeAutoImportConfigs: [],
            contentTypeRequiredFields: [],
            contentTypeUniqueFields: [],
            originalConfigs: [],
            open: false,
            loading: false,
            AutoImportSources: AutoImportSources,
            contentType: new ContentTypeForAutoImportConfigDTO(),
            services: ContentTypeAutoImportConfigService.getContentTypeAutoImportConfig.call(this),
            activePanel: null,
            loadingStates: [false, false]
        };
    },
    mixins: [validationMixin],
    validations: {
        contentTypeAutoImportConfigs: {
            $each: {
                description: { required },
                serviceID: { required },
                rootFolder: {
                    required: requiredIf(function (model) {
                        return (
                            model.serviceID === AutoImportSources.EmailOffice365 ||
                            model.serviceID === AutoImportSources.OneDrive ||
                            model.serviceID === AutoImportSources.Email ||
                            model.serviceID === AutoImportSources.FileSystem ||
                            model.serviceID === AutoImportSources.GoogleDrive ||
                            model.serviceID === AutoImportSources.Imputations
                        );
                    }),
                }
            },
        },
    },
    async beforeRouteLeave(to, from, next) {
        const changedItems = this.getChangedConfigs();

        if (changedItems.length === 0 || to.fullPath == '/login') {
            this.open = false;
            return next();
        }
        try {
            this.open = true;
            const res = await this.unsavedChangesMessageConfirm();
            if (res === 1) {
                return next(false);
            }
            return next();
        } catch (e) {
            console.error('Error while saving changed items', e);
            next(false);
            this.open = true;
        }
    },
    computed: {
        pageTitle() {
            return `${this.$t('AutoImportConfig')} - ${this.contentType.name} ${this.contentType.version}`;
        },
        currentChildLoading() {
            return this.loadingStates[this.activePanel];
        }

    },
    mounted() {
        this.loadData();
    },
    watch: {
        // For ContentTypeID route change inside the component:
        '$route.params.id': {
            immediate: true,
            handler(newId, oldId) {
                if (oldId && newId !== oldId) {
                    this.loadData();
                }
            }
        }
    },
    methods: {
        loadData() {
            this.loading = true;
            const id = this.$route.params.id;
            if (id) {
                ContentTypeService.getContentTypeForAutoImport(id)
                    .then((data) => {
                        this.edit(data);
                    })
                    .catch((error) => {
                        console.error('Error loading data:', error);
                        this.close();
                    });
            }
        },
        descriptionErrors(index) {
            const errors = [];
            if (!this.$v.contentTypeAutoImportConfigs.$each[index].description.$dirty)
                return errors;
            !this.$v.contentTypeAutoImportConfigs.$each[index].description.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        serviceErrors(index) {
            const errors = [];
            if (!this.$v.contentTypeAutoImportConfigs.$each[index].serviceID.$dirty)
                return errors;
            !this.$v.contentTypeAutoImportConfigs.$each[index].serviceID.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        close() {
            this.$router.push({ name: 'ContentTypeList' });
        },
        edit(dto) {
            this.contentType = new ContentTypeForAutoImportConfigDTO(dto);
            this.hasAutomaticRecognition = this.contentType.hasAutomaticRecognition;
            this.getAutoImportConfigs();
            this.contentTypeRequiredFields = this.contentType.defFields.filter((field) => field.IsRequired);
            this.contentTypeUniqueFields = this.contentType.defFields.filter((field) => field.IsUnique);
        },
        getAutoImportConfigs(editedAutorecognitionConfigId, editedItemUuid) {
            this.loading = true;
            ContentTypeService.getAutoImportConfigs(this.contentType.id)
                .then((data) => {
                    if (editedAutorecognitionConfigId !== undefined && editedItemUuid !== undefined) {
                        // Find the item by UUID to handle cases where multiple items have id=0
                        const editedItemInCurrentState = this.contentTypeAutoImportConfigs.find(t => t.uuid === editedItemUuid);

                        if (!editedItemInCurrentState) {
                            this.loading = false;
                            return;
                        }

                        if (editedAutorecognitionConfigId === 0) {
                            // New item was saved - backend assigned a real ID
                            // Find which ID in backend response doesn't exist in originalConfigs (that's the new one)
                            const existingIds = new Set(this.originalConfigs.map(item => item.id));
                            const newItemFromBackend = data.find(dto => !existingIds.has(dto.id));

                            if (newItemFromBackend) {
                                // Update the ID in the item we saved (identified by UUID)
                                editedItemInCurrentState.id = newItemFromBackend.id;

                                // Create the config DTO from backend response and preserve the UUID
                                const newConfigDto = new ContentTypeAutoImportConfig(newItemFromBackend);
                                newConfigDto.uuid = editedItemInCurrentState.uuid;

                                // Update the current item with backend data while keeping the UUID
                                Object.assign(editedItemInCurrentState, newConfigDto);

                                // Add to originalConfigs with the same UUID
                                this.originalConfigs.push(JSON.parse(JSON.stringify(newConfigDto)));
                            }
                        } else {
                            // Existing item - find it in backend response by ID
                            const updatedItemFromBackend = data.find(dto => dto.id === editedAutorecognitionConfigId);

                            if (updatedItemFromBackend) {
                                // Create config DTO and preserve the UUID
                                const updatedConfigDto = new ContentTypeAutoImportConfig(updatedItemFromBackend);
                                updatedConfigDto.uuid = editedItemInCurrentState.uuid;

                                // Update the current item
                                Object.assign(editedItemInCurrentState, updatedConfigDto);

                                // Update originalConfigs - match by UUID to update the correct item
                                this.originalConfigs = this.originalConfigs.map(item =>
                                    item.uuid === editedItemInCurrentState.uuid ? JSON.parse(JSON.stringify(updatedConfigDto)) : item
                                );
                            }
                        }
                    } else {
                        this.contentTypeAutoImportConfigs = data.map(
                            (dto) => new ContentTypeAutoImportConfig(dto)
                        );

                        this.originalConfigs = JSON.parse(JSON.stringify(this.contentTypeAutoImportConfigs));
                    }

                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        async save(item) {
            this.loading = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.loading = false;
                return;
            }
            const incompleteConfig =
                (item.serviceID == AutoImportSources.Email && item.emailAccount == '') ||
                (item.serviceID == AutoImportSources.OneDrive && item.oAuthRefreshToken == '') ||
                (item.serviceID == AutoImportSources.EmailOffice365 && item.oAuthRefreshToken == '');

            if (incompleteConfig) {
                this.loading = false;
                Utils.toast.error(this.$t('NullParametersEmail'));
                return;
            }

            if (this.hasAutomaticRecognition === false) {
                const requiredFieldsCodes = this.contentTypeRequiredFields.map(
                    (requiredField) => requiredField.Code
                );

                const emptyRequiredFields = item.fields
                    .filter(
                        (field) => !field.value && requiredFieldsCodes.includes(field.defFieldCode)
                    )
                    .map((field) => {
                        const matchingRequired = this.contentTypeRequiredFields.find(
                            (required) => required.Code === field.defFieldCode
                        );
                        return matchingRequired ? matchingRequired.Name : field.defFieldCode;
                    });

                if (emptyRequiredFields.length > 0) {
                    const fieldNamesString = emptyRequiredFields.join(', ');
                    Utils.toast.error(
                        this.$t('PendingFieldsConfiguration') +
                            fieldNamesString +
                            this.$t('NoAutomaticRecognition')
                    );
                    this.loading = false;
                    return;
                }
            }

            const uniqueFieldsCodes = this.contentTypeUniqueFields.map(
                (uniqueField) => uniqueField.Code
            );

            const filledUniqueFields = item.fields
                .filter((field) => field.value != '' && uniqueFieldsCodes.includes(field.defFieldCode))
                .map((field) => {
                    const match = this.contentTypeUniqueFields.find(
                        (uf) => uf.Code === field.defFieldCode
                    );
                    return match ? match.Name : field.defFieldCode;
                });

            if (filledUniqueFields.length > 0) {
                const fieldNamesString = filledUniqueFields.join(', ');
                Utils.toast.error(
                    this.$t('UniqueFieldsWithValue') +
                        fieldNamesString
                );
                this.loading = false;
                return;
            }

            const request = item.toDto();
            if (item.id > 0) {
                try {
                    await ContentTypeService.updateAutoImportConfigs(request);
                    this.loading = false;
                } catch (error) {
                    console.error('Error saving data:', error);
                    this.loading = false;
                    return false;
                }
            } else {
                try {
                    await ContentTypeService.saveAutoImportConfigs(request);
                    this.loading = false;
                } catch (error) {
                    console.error('Error saving data:', error);
                    this.loading = false;
                    return false;
                }
            }

            Utils.toast.success(this.$t('SavedSuccessfully'));
            this.getAutoImportConfigs(item.id, item.uuid);
            return true;
        },
        addContentTypeAutoImportConfig() {
            const newConfig = new ContentTypeAutoImportConfig();
            newConfig.serviceID = this.services[0].id;
            newConfig.contentTypeID = this.contentType.id;
            this.contentTypeAutoImportConfigs.push(newConfig);
        },
        removeContentTypeAutoImportConfig(autoImportConfig) {
            if (autoImportConfig.id === 0) {
                this.contentTypeAutoImportConfigs = this.contentTypeAutoImportConfigs.filter((item) => item.uuid !== autoImportConfig.uuid);
            } else {
                this.$confirm(this.$t('ItemDeleteConfirmation'), {
                    title: this.$t('Delete'),
                    icon: 'mdi-alert',
                    color: 'red',
                    buttonTrueText: this.$t('Delete'),
                    buttonFalseText: this.$t('Cancel'),
                }).then((res) => {
                    if (res == 1) {
                        this.loading = true;
                        ContentTypeService.deleteAutoImportConfigs(autoImportConfig.id)
                            .then(() => {
                                Utils.toast.success(this.$t('DeletedSuccessfully'));
                                this.getAutoImportConfigs();
                                this.loading = false;
                            })
                            .catch(() => {
                                this.loading = false;
                            });
                    }
                });
            }
        },
        editFields(item) {
            this.currentItem = item;
            this.$refs[this.fieldsModalKey].edit(item.fields);
        },
        onFieldsSaved(fields) {
            this.currentItem.fields = fields;
        },
        resetValues(item) {
            item.description = '';
            item.rootFolder = '';
            item.folderList = [];
            item.nameFolder = '';
            item.emailAccount = '';
            item.emailPassword = '';
            item.emailPort = '';
            item.emailServer = '';
            item.emailUseSSL = 'false';
            item.oAuthAccessToken = '';
            item.oAuthRefreshToken = '';
            item.oneDriveSubscriptionId = '';
            item.oneDriveSubscriptionExpirationDate = '';
            item.alphaNumericSeparator = '';
        },
        addSeparator(item) {
            const itemId = item.id > 0 ? item.id : item.uuid;
            this.$refs[this.alphaNumericSeparatorModalKey].show(itemId, item.alphaNumericSeparator);
        },
        saveAlphaNumericSeparator(separator, itemId) {
            this.contentTypeAutoImportConfigs.forEach((item) => {
                if (item.id == itemId || item.uuid == itemId) {
                    item.alphaNumericSeparator = separator;
                }
            });
        },
        getServiceName(serviceID) {
            const service = this.services.find(item => item.id == serviceID);
            return service ? service.name : '';
        },
        saveEmailConfig(autoImportConfig) {
            this.contentTypeAutoImportConfigs.forEach((item) => {
                if (item.id == autoImportConfig.id || item.uuid == autoImportConfig.uuid) {
                    item.emailAccount = autoImportConfig.emailAccount;
                    item.emailPassword = autoImportConfig.emailPassword;
                    item.emailPort = autoImportConfig.emailPort;
                    item.emailServer = autoImportConfig.emailServer;
                    item.emailUseSSL = autoImportConfig.emailUseSSL;
                }
            });
        },
        onFolderSelected(autoImportConfig) {
            this.contentTypeAutoImportConfigs.forEach((item) => {
                if (item.id == autoImportConfig.id || item.uuid == autoImportConfig.uuid) {
                    item.rootFolder = autoImportConfig.rootFolder;
                    item.nameFolder = autoImportConfig.nameFolder;
                }
            });
        },
        isConfigChanged(currentItem, originalItem) {
            if (!originalItem) return true;

            const keysToCompare = [
                'description',
                'serviceID',
                'rootFolder',
                'emailAccount',
                'emailPassword',
                'emailPort',
                'emailServer',
                'emailUseSSL',
                'oAuthAccessToken',
                'oAuthRefreshToken',
                'oneDriveSubscriptionId',
                'oneDriveSubscriptionExpirationDate',
                'alphaNumericSeparator',
                'convertImageToPdf',
                'convertOfficeToPdf',
                'fields',
            ];

            for (const key of keysToCompare) {
                const currentVal = JSON.stringify(currentItem[key]);
                const originalVal = JSON.stringify(originalItem[key]);
                if (currentVal !== originalVal) return true;
            }

            return false;
        },
        getChangedConfigs() {
            return this.contentTypeAutoImportConfigs.filter(currentItem => {
                const originalItem = this.originalConfigs.find(
                    o => o.id === currentItem.id || o.uuid === currentItem.uuid
                );
                return this.isConfigChanged(currentItem, originalItem);
            });
        },
        async unsavedChangesMessageConfirm() {
            return new Promise((resolve) => {
                this.$confirm(this.$t('UnsavedChanges'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Cancel'),
                    buttonFalseText: this.$t('DiscardChanges')
                }).then((res) => {
                    resolve(res);
                });
            });
        },

    },
};
</script>
<style scoped>
.padding-sm {
  padding-top: 4px;
  padding-bottom: 4px;
}
.autoimport_header-buttons {
    justify-content: flex-end;
    align-items: center;
    padding: 0px 20px;
}
</style>
